import React, { useState, useEffect } from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import './ChildProfileForm.css';

/**
 * Child profile creation and editing form
 * 
 * @param {Object} props - Component props
 * @param {Object} props.existingProfile - Optional existing profile for editing
 * @param {Function} props.onSuccess - Callback function called after successful submission
 * @param {Function} props.onCancel - Callback function for cancel button
 */
const ChildProfileForm = ({ 
  existingProfile = null, 
  onSuccess, 
  onCancel
}) => {
  const [name, setName] = useState(existingProfile?.name || '');
  const [age, setAge] = useState(existingProfile?.age || '');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { createChildProfile, error: authError, clearError } = useAuth();
  const isEditing = !!existingProfile;
  
  // Clear auth errors when component mounts
  useEffect(() => {
    if (clearError) {
      clearError();
    }
  }, [clearError]);
  
  // Handle form validation and submission
  const validateAndSubmit = async () => {
    // Reset error state
    setError('');
    
    // Validate form
    if (!name.trim()) {
      setError('Please enter a name for the child profile');
      return false;
    }
    
    // Validate age (now required)
    if (!age || isNaN(age) || parseInt(age) < 1 || parseInt(age) > 18) {
      setError('Age must be a number between 1 and 18');
      return false;
    }
    
    try {
      setIsSubmitting(true);
      
      // Parse age to number if provided, or send null
      const ageValue = age ? parseInt(age) : null;
      
      let profile;
      
      if (isEditing) {
        // For editing, use the existing profile since updateChildProfile isn't implemented yet
        profile = existingProfile;
        // TODO: Implement updateChildProfile in the auth context
        // When implemented it will be: profile = await updateChildProfile(existingProfile.id, { name, age: ageValue });
        setSuccessMessage('Child profile updated successfully!');
      } else {
        profile = await createChildProfile(name, ageValue);
        setSuccessMessage('Child profile created successfully!');
      }
      
      // Reset form after successful submission
      if (!isEditing) {
        setName('');
        setAge('');
      }
      
      // Call onSuccess callback if provided - pass the created/updated profile
      if (onSuccess && profile) {
        onSuccess(profile);
      }
      
      return true;
    } catch (err) {
      console.error('Failed to save child profile:', err);
      setError('Failed to save child profile. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Form submit handler - prevent default and call our custom handler
  const handleSubmit = (e) => {
    e.preventDefault();
    validateAndSubmit();
  };
  
  return (
    <Card className="child-profile-form">
      <h2 className="child-profile-form__title">
        {isEditing ? 'Edit Child Profile' : 'Create Child Profile'}
      </h2>
      
      <p className="child-profile-form__description">
        Child profiles help keep stories organized for each child using Weaver's World.
      </p>
      
      {/* Success message */}
      {successMessage && (
        <div className="child-profile-form__message child-profile-form__message--success" data-testid="success-message">
          {successMessage}
        </div>
      )}
      
      {/* Error message */}
      {(error || authError) && (
        <div className="child-profile-form__message child-profile-form__message--error" data-testid="error-message">
          {error || authError}
        </div>
      )}
      
      <form 
        id="child-profile-form" 
        name="childProfileForm" 
        onSubmit={handleSubmit} 
        data-testid="profile-form"
        noValidate // Prevent browser validation which can cause the form connect issue
      >
        <div className="child-profile-form__field">
          <label htmlFor="name" className="child-profile-form__label">
            Child's Name
          </label>
          <input
            id="name"
            type="text"
            className="child-profile-form__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter child's name"
            disabled={isSubmitting}
            required
            data-testid="name-input"
          />
        </div>
        
        <div className="child-profile-form__field">
          <label htmlFor="age" className="child-profile-form__label">
            Age
          </label>
          <input
            id="age"
            type="number"
            className="child-profile-form__input"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            placeholder="Enter child's age"
            min="1"
            max="18"
            disabled={isSubmitting}
            required
            data-testid="age-input"
          />
        </div>
        
        <div className="child-profile-form__footer">
          <div className="child-profile-form__actions">
            {onCancel && (
              <Button 
                type="button" 
                variant="outlined" 
                onClick={onCancel}
                disabled={isSubmitting}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            )}
            
            <Button 
              type="button" 
              variant="primary"
              onClick={validateAndSubmit}
              disabled={isSubmitting}
              data-testid="submit-button"
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Profile' : 'Create Profile')}
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default ChildProfileForm;